// ┏━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┓
// ┃ ██████ ██████ ██████       █      █      █      █      █ █▄  ▀███ █       ┃
// ┃ ▄▄▄▄▄█ █▄▄▄▄▄ ▄▄▄▄▄█  ▀▀▀▀▀█▀▀▀▀▀ █ ▀▀▀▀▀█ ████████▌▐███ ███▄  ▀█ █ ▀▀▀▀▀ ┃
// ┃ █▀▀▀▀▀ █▀▀▀▀▀ █▀██▀▀ ▄▄▄▄▄ █ ▄▄▄▄▄█ ▄▄▄▄▄█ ████████▌▐███ █████▄   █ ▄▄▄▄▄ ┃
// ┃ █      ██████ █  ▀█▄       █ ██████      █      ███▌▐███ ███████▄ █       ┃
// ┣━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┫
// ┃ Copyright (c) 2017, the Perspective Authors.                              ┃
// ┃ ╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌ ┃
// ┃ This file is part of the Perspective library, distributed under the terms ┃
// ┃ of the [Apache License 2.0](https://www.apache.org/licenses/LICENSE-2.0). ┃
// ┗━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┛

export interface Signal<T> {
    /** The current value. */
    get(): T;

    /** Replace the value and notify every subscriber. */
    set(value: T): void;

    /** Subscribe, invoked immediately with the current value. */
    subscribe(listener: (value: T) => void): () => void;
}

/**
 * A minimal observable value — the one pub/sub shape the data modules share.
 * Listeners are notified with the value itself; treat it as an immutable
 * snapshot and call `set` with a replacement rather than mutating in place.
 *
 * @param initial the starting value.
 */
export function signal<T>(initial: T): Signal<T> {
    let value = initial;
    const listeners = new Set<(value: T) => void>();
    return {
        get: () => value,

        set(next: T) {
            value = next;
            for (const listener of listeners) {
                listener(next);
            }
        },

        subscribe(listener: (value: T) => void) {
            listeners.add(listener);
            listener(value);
            return () => {
                listeners.delete(listener);
            };
        },
    };
}
